/**
 * Async store implementations that communicate with the worker.
 * These provide the same interface as the sync stores but return Promises.
 */

import { OfflineWorkerClient } from './worker/client.js';

// ============================================================================
// Types
// ============================================================================

export interface StoredDocument {
    id: string;
    v: number;
    p: string;
    f_id: string | null;
    f_v: number | null;
    name: string | null;
    json: string;
}

export interface StoredForm {
    id: string;
    v: number;
    json: string;
}

export interface StoredChangeSet {
    seq: number;
    json: string;
}

export interface StoredUpload {
    uri_old: string;
    uri_new: string | null;
    size: number | null;
    mime: string | null;
    thumbnail: Uint8Array | null;
    status: 'pending' | 'uploading' | 'done' | 'failed';
    change_seq: number;
    entry_idx: number;
    field_path: string;
}

export interface AddUploadOptions {
    uri_old: string;
    size?: number;
    mime?: string;
    thumbnail?: Uint8Array;
    change_seq: number;
    entry_idx: number;
    field_path: string;
}

// ============================================================================
// Documents Store
// ============================================================================

/**
 * Async document store that communicates with the worker.
 */
export class DocumentsStoreAsync {
    constructor(private client: OfflineWorkerClient) {}

    /**
     * Save a document to the store.
     */
    async save(doc: {
        id: string;
        v: number;
        p?: string;
        f_id?: string | null;
        f_v?: number | null;
        name?: string | null;
        json: string;
    }): Promise<void> {
        await this.client.run(
            `INSERT OR REPLACE INTO docs (id, v, p, f_id, f_v, name, json) VALUES (?, ?, ?, ?, ?, ?, ?)`,
            [doc.id, doc.v, doc.p ?? '', doc.f_id ?? null, doc.f_v ?? null, doc.name ?? null, doc.json]
        );
    }

    /**
     * Get the latest version of a document by ID.
     */
    async getLatest(id: string): Promise<StoredDocument | null> {
        const rows = await this.client.exec<StoredDocument>(
            `SELECT id, v, p, f_id, f_v, name, json FROM docs WHERE id = ? ORDER BY v DESC LIMIT 1`,
            [id]
        );
        return rows[0] ?? null;
    }

    /**
     * Get a specific version of a document.
     */
    async getVersion(id: string, v: number): Promise<StoredDocument | null> {
        const rows = await this.client.exec<StoredDocument>(
            `SELECT id, v, p, f_id, f_v, name, json FROM docs WHERE id = ? AND v = ?`,
            [id, v]
        );
        return rows[0] ?? null;
    }

    /**
     * Get all documents with a specific parent path.
     */
    async getByParent(p: string): Promise<StoredDocument[]> {
        return this.client.exec<StoredDocument>(
            `SELECT id, v, p, f_id, f_v, name, json FROM docs WHERE p = ?`,
            [p]
        );
    }

    /**
     * Get all descendants of a root path (recursive walk).
     */
    async getAllDescendants(root: string = ''): Promise<StoredDocument[]> {
        if (root === '') {
            return this.client.exec<StoredDocument>(
                `SELECT id, v, p, f_id, f_v, name, json FROM docs`
            );
        }
        return this.client.exec<StoredDocument>(
            `SELECT id, v, p, f_id, f_v, name, json FROM docs WHERE p = ? OR p LIKE ?`,
            [root, root + '/%']
        );
    }

    /**
     * Find documents by foreign key reference.
     */
    async findByForeign(f_id: string, f_v?: number): Promise<StoredDocument[]> {
        if (f_v !== undefined) {
            return this.client.exec<StoredDocument>(
                `SELECT id, v, p, f_id, f_v, name, json FROM docs WHERE f_id = ? AND f_v = ?`,
                [f_id, f_v]
            );
        }
        return this.client.exec<StoredDocument>(
            `SELECT id, v, p, f_id, f_v, name, json FROM docs WHERE f_id = ?`,
            [f_id]
        );
    }

    /**
     * Delete a document by ID.
     */
    async delete(id: string): Promise<void> {
        await this.client.run(`DELETE FROM docs WHERE id = ?`, [id]);
    }

    /**
     * Count all documents.
     */
    async count(): Promise<number> {
        return this.client.execScalar<number>(`SELECT COUNT(*) FROM docs`);
    }
}

// ============================================================================
// Forms Store
// ============================================================================

/**
 * Async forms store that communicates with the worker.
 */
export class FormsStoreAsync {
    constructor(private client: OfflineWorkerClient) {}

    /**
     * Save a form to the store.
     */
    async save(id: string, v: number, data: unknown): Promise<void> {
        const json = typeof data === 'string' ? data : JSON.stringify(data);
        await this.client.run(
            `INSERT OR REPLACE INTO forms (id, v, json) VALUES (?, ?, ?)`,
            [id, v, json]
        );
    }

    /**
     * Get a specific version of a form.
     */
    async get(id: string, v: number): Promise<unknown | null> {
        const rows = await this.client.exec<StoredForm>(
            `SELECT id, v, json FROM forms WHERE id = ? AND v = ?`,
            [id, v]
        );
        if (rows.length === 0) return null;
        
        try {
            return JSON.parse(rows[0].json);
        } catch {
            return rows[0].json;
        }
    }

    /**
     * Get the latest version of a form.
     */
    async getLatest(id: string): Promise<unknown | null> {
        const rows = await this.client.exec<StoredForm>(
            `SELECT id, v, json FROM forms WHERE id = ? ORDER BY v DESC LIMIT 1`,
            [id]
        );
        if (rows.length === 0) return null;
        
        try {
            return JSON.parse(rows[0].json);
        } catch {
            return rows[0].json;
        }
    }

    /**
     * Get all versions of a form.
     */
    async getAllVersions(id: string): Promise<Array<{ v: number; data: unknown }>> {
        const rows = await this.client.exec<StoredForm>(
            `SELECT id, v, json FROM forms WHERE id = ? ORDER BY v ASC`,
            [id]
        );
        return rows.map(row => ({
            v: row.v,
            data: (() => {
                try { return JSON.parse(row.json); } 
                catch { return row.json; }
            })()
        }));
    }

    /**
     * Delete a form (optionally a specific version).
     */
    async delete(id: string, v?: number): Promise<void> {
        if (v !== undefined) {
            await this.client.run(`DELETE FROM forms WHERE id = ? AND v = ?`, [id, v]);
        } else {
            await this.client.run(`DELETE FROM forms WHERE id = ?`, [id]);
        }
    }

    /**
     * Count all forms.
     */
    async count(): Promise<number> {
        return this.client.execScalar<number>(`SELECT COUNT(*) FROM forms`);
    }
}

// ============================================================================
// Changes Store
// ============================================================================

/**
 * Async changes store that communicates with the worker.
 */
export class ChangesStoreAsync {
    constructor(private client: OfflineWorkerClient) {}

    /**
     * Append a change set to the queue.
     * @returns The sequence number of the inserted change set.
     */
    async appendChangeSet(changeSet: unknown): Promise<number> {
        const json = typeof changeSet === 'string' ? changeSet : JSON.stringify(changeSet);
        await this.client.run(`INSERT INTO changes (json) VALUES (?)`, [json]);
        return this.client.execScalar<number>(`SELECT last_insert_rowid()`);
    }

    /**
     * Get all change sets in order.
     */
    async getAllChangeSets(): Promise<Array<{ seq: number; json: unknown }>> {
        const rows = await this.client.exec<StoredChangeSet>(
            `SELECT seq, json FROM changes ORDER BY seq ASC`
        );
        return rows.map(row => ({
            seq: row.seq,
            json: (() => {
                try { return JSON.parse(row.json); } 
                catch { return row.json; }
            })()
        }));
    }

    /**
     * Get a specific change set.
     */
    async getChangeSet(seq: number): Promise<{ seq: number; json: unknown } | null> {
        const rows = await this.client.exec<StoredChangeSet>(
            `SELECT seq, json FROM changes WHERE seq = ?`,
            [seq]
        );
        if (rows.length === 0) return null;
        
        const row = rows[0];
        return {
            seq: row.seq,
            json: (() => {
                try { return JSON.parse(row.json); } 
                catch { return row.json; }
            })()
        };
    }

    /**
     * Delete a change set and its associated uploads.
     */
    async deleteChangeSet(seq: number): Promise<void> {
        await this.client.run(`DELETE FROM uploads WHERE change_seq = ?`, [seq]);
        await this.client.run(`DELETE FROM changes WHERE seq = ?`, [seq]);
    }

    /**
     * Add an upload record.
     */
    async addUpload(opts: AddUploadOptions): Promise<void> {
        await this.client.run(
            `INSERT INTO uploads (uri_old, size, mime, thumbnail, change_seq, entry_idx, field_path, status)
             VALUES (?, ?, ?, ?, ?, ?, ?, 'pending')`,
            [
                opts.uri_old,
                opts.size ?? null,
                opts.mime ?? null,
                opts.thumbnail ?? null,
                opts.change_seq,
                opts.entry_idx,
                opts.field_path
            ]
        );
    }

    /**
     * Update an upload's URI and status.
     */
    async updateUploadUri(uri_old: string, uri_new: string, status: StoredUpload['status'] = 'done'): Promise<void> {
        await this.client.run(
            `UPDATE uploads SET uri_new = ?, status = ? WHERE uri_old = ?`,
            [uri_new, status, uri_old]
        );
    }

    /**
     * Update an upload's status.
     */
    async updateUploadStatus(uri_old: string, status: StoredUpload['status']): Promise<void> {
        await this.client.run(
            `UPDATE uploads SET status = ? WHERE uri_old = ?`,
            [status, uri_old]
        );
    }

    /**
     * Get all pending uploads.
     */
    async getPendingUploads(): Promise<StoredUpload[]> {
        return this.client.exec<StoredUpload>(
            `SELECT uri_old, uri_new, size, mime, thumbnail, status, change_seq, entry_idx, field_path 
             FROM uploads WHERE status = 'pending' ORDER BY change_seq ASC, entry_idx ASC`
        );
    }

    /**
     * Get all uploads for a specific change set.
     */
    async getUploadsForChangeSet(change_seq: number): Promise<StoredUpload[]> {
        return this.client.exec<StoredUpload>(
            `SELECT uri_old, uri_new, size, mime, thumbnail, status, change_seq, entry_idx, field_path 
             FROM uploads WHERE change_seq = ? ORDER BY entry_idx ASC`,
            [change_seq]
        );
    }

    /**
     * Get an upload by its original URI.
     */
    async getUpload(uri_old: string): Promise<StoredUpload | null> {
        const rows = await this.client.exec<StoredUpload>(
            `SELECT uri_old, uri_new, size, mime, thumbnail, status, change_seq, entry_idx, field_path 
             FROM uploads WHERE uri_old = ?`,
            [uri_old]
        );
        return rows[0] ?? null;
    }

    /**
     * Delete an upload.
     */
    async deleteUpload(uri_old: string): Promise<void> {
        await this.client.run(`DELETE FROM uploads WHERE uri_old = ?`, [uri_old]);
    }
}

// ============================================================================
// Factory
// ============================================================================

/**
 * Container for all async offline stores.
 */
export interface OfflineStoresAsync {
    /** Store for documents */
    documents: DocumentsStoreAsync;
    /** Store for forms */
    forms: FormsStoreAsync;
    /** Store for changes and uploads */
    changes: ChangesStoreAsync;
    /** Delete all data from all stores */
    deleteAll: () => Promise<void>;
    /** Close the worker connection */
    close: () => Promise<void>;
}

/**
 * Result from creating offline stores.
 */
export interface CreateOfflineStoresResult {
    stores: OfflineStoresAsync;
    workerClient: OfflineWorkerClient;
    needsRestore: boolean;
}

/**
 * Creates offline stores using a Web Worker with OPFS-backed SQLite.
 * 
 * @param workerUrl - URL to the worker script (from options.offlineStoreWorkerPath)
 * @param sqliteImportPath - URL to the sqlite-wasm module (from options.sqliteImportPath)
 * @param prefix - Database file prefix (default: 'aeppic-offline')
 * @returns Container with all async stores and the worker client
 * 
 * @example
 * ```ts
 * const { stores, workerClient, needsRestore } = await createOfflineStoresAsync(
 *     '/libs/aeppic/offline-store-worker.js',
 *     '/libs/sqlite/sqlite3.mjs',
 *     'myapp'
 * );
 * 
 * if (needsRestore) {
 *     // Restore from backup
 * }
 * 
 * await stores.documents.save({ id: '123', v: 1, json: '{}' });
 * const doc = await stores.documents.getLatest('123');
 * 
 * await stores.close();
 * ```
 */
export async function createOfflineStoresAsync(
    workerUrl: string,
    sqliteImportPath: string,
    prefix: string = 'aeppic-offline'
): Promise<CreateOfflineStoresResult> {
    const client = new OfflineWorkerClient();
    const { needsRestore } = await client.init(workerUrl, sqliteImportPath, prefix);

    const documents = new DocumentsStoreAsync(client);
    const forms = new FormsStoreAsync(client);
    const changes = new ChangesStoreAsync(client);

    const stores: OfflineStoresAsync = {
        documents,
        forms,
        changes,
        deleteAll: () => client.deleteAll(),
        close: () => client.close()
    };

    return { stores, workerClient: client, needsRestore };
}
